<script setup>
/******************************************************************************
 * PageComponent: MeterRegister (Zählwerk) reading value: double input
 *****************************************************************************/
import { ref, onBeforeMount } from "vue";
import { useReadingStore } from "Stores/reading";
import { parseReadingInput, validateKeyDown } from "@/handlers/useReading";
const data = useReadingStore();
const props = defineProps({
    meterRegisterId: {
        type: String,
        required: true,
    },
});
const emit = defineEmits(["change"]);
const meterRegisterData = data.getMeterRegisterData(props.meterRegisterId);
const readingValuePre = ref("");
const readingValuePost = ref("");
const postInput = ref(null);
/**
 * @function on key down, check if the pressed key is valid
 * @param {KeyboardEvent} event
 */
const onKeyDownPre = (event) =>
    validateKeyDown(
        event,
        meterRegisterData.preCommaDigits,
        readingValuePre.value?.length
    );
/**
 * @function on key down, check if the pressed key is valid
 * @param {KeyboardEvent} event
 */
const onKeyDownPost = (event) =>
    validateKeyDown(
        event,
        meterRegisterData.postCommaDigits,
        readingValuePost.value?.length
    );
/**
 * @function emit change so the reading value in the store gets updated.
 */
const emitValue = () => {
    if (readingValuePost.value?.length > 0) {
        emit("change", `${readingValuePre.value}.${readingValuePost.value}`);
    } else {
        emit("change", `${readingValuePre.value}`);
    }
};
/**
 * @function digits before the comma, typed or pasted. Decimals after a comma ("8.814,5")
 * go into the post comma input.
 * @param {String} text
 */
const setPre = (text) => {
    const { pre, post, hasDecimals } = parseReadingInput(text);
    readingValuePre.value = pre.slice(0, meterRegisterData.preCommaDigits);
    if (hasDecimals) {
        if (post.length > 0)
            readingValuePost.value = post.slice(
                0,
                meterRegisterData.postCommaDigits
            );
        postInput.value?.focus();
    }
    emitValue();
};
/**
 * @function digits after the comma, typed or pasted.
 * @param {String} text
 */
const setPost = (text) => {
    readingValuePost.value = text
        .replace(/\D/g, "")
        .slice(0, meterRegisterData.postCommaDigits);
    emitValue();
};
/**
 * @function on input (typing, pasting, autofill), take over the cleaned value
 * and show it in the input.
 * @param {InputEvent} event
 */
const onInputPre = (event) => {
    setPre(event.target.value);
    event.target.value = readingValuePre.value;
};
const onInputPost = (event) => {
    setPost(event.target.value);
    event.target.value = readingValuePost.value;
};
/**
 * @function on paste, read the whole clipboard text - the browser would cut it at maxlength.
 * @param {ClipboardEvent} event
 */
const onPastePre = (event) => {
    event.preventDefault();
    setPre(event.clipboardData?.getData("text") ?? "");
};
const onPastePost = (event) => {
    event.preventDefault();
    setPost(event.clipboardData?.getData("text") ?? "");
};

/**
 * @hook before mount, update the refs with values from store
 */
onBeforeMount(() => {
    const readingValue = data.getReadingValue(props.meterRegisterId).split(".");
    readingValuePre.value = readingValue[0];
    readingValuePost.value = readingValue[1];
});
</script>

<template>
    <div class="pre">
        <input
            :id="`meterReading-${meterRegisterId}`"
            :value="readingValuePre"
            class="form-input form-input--meter"
            type="text"
            inputmode="numeric"
            :maxlength="meterRegisterData.preCommaDigits"
            formnovalidate
            @keydown="onKeyDownPre"
            @input="onInputPre"
            @paste="onPastePre"
        />
        <ul class="digits">
            <li
                v-for="index in meterRegisterData.preCommaDigits"
                :key="index"
                class="digit"
            />
        </ul>
    </div>
    <div class="comma">,</div>
    <div class="post">
        <input
            :id="`meterReading-${meterRegisterId}-postcomma`"
            ref="postInput"
            :value="readingValuePost"
            class="form-input form-input--has-addon form-input--meter"
            type="text"
            inputmode="numeric"
            :maxlength="meterRegisterData.postCommaDigits"
            formnovalidate
            aria-label="Nachkommastellen"
            @keydown="onKeyDownPost"
            @input="onInputPost"
            @paste="onPastePost"
        />
        <div
            class="form-input-addon form-input-addon--meter"
            aria-label="Einheit"
        >
            {{ meterRegisterData.meterUnit }}
        </div>
        <ul class="digits">
            <li
                v-for="index in meterRegisterData.postCommaDigits"
                :key="index"
                class="digit"
            />
        </ul>
    </div>
</template>

<style lang="scss" scoped>
$comma-width: 10px;
$addon-width: 46px;

.pre {
    display: flex;
    position: relative;

    width: 50%;

    .form-input {
        flex-grow: 1;
    }

    .digits {
        padding-right: 21px;
    }
}

.post {
    display: flex;
    position: relative;

    width: calc(50% - $comma-width);

    .form-input {
        flex-grow: 1;
    }

    .form-input-addon {
        flex: 0 0 $addon-width;
    }

    .digits {
        padding-right: #{$addon-width + 19px};
    }
}

input[type="text"] {
    z-index: get($z, "form");

    width: 100%;
}

.comma {
    flex: 0 0 10px;

    font-size: #{get($sizes, "base") * 1.5};
    font-weight: bold;
    text-align: center;
}
</style>
